import DOMPurify from 'dompurify';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Drawer, Select, MenuItem, FormControl } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { proposePromptUpdate } from '../../../store/Agent';
import './PromptAIUpdateDrawer.css';

const PromptAIUpdateDrawer = ({ isOpen, onClose, agentId, onApprove }) => {
  const dispatch = useDispatch();
  const [ui, setUi] = useState(INITIAL_UI);
  const updateUi = (patch) => setUi((prev) => ({ ...prev, ...patch }));

  const handleGenerate = () => {
    if (!ui['feedback'].trim() || ui['isProposing']) return;
    updateUi({ isProposing: true });
    dispatch(proposePromptUpdate({
      agentId,
      feedback: ui['feedback'],
      previousProposal: ui['proposal'] || null,
      model: ui['model'],
      successCallback: (proposedScript) => setUi((prev) => ({ ...prev, proposal: proposedScript, feedbackTrail: [...prev['feedbackTrail'], prev['feedback']], feedback: '', isProposing: false })),
      failCallback: () => updateUi({ isProposing: false }),
    }));
  };

  const handleApply = () => {
    if (!ui['proposal']) return;
    onApprove(ui['proposal'], ui['feedbackTrail'].join('\n'));
    setUi(INITIAL_UI);
    onClose();
  };

  const handleDiscard = () => {
    setUi(INITIAL_UI);
    onClose();
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose} className="agent-prompt-ai">
      <div className="agent-prompt-ai-panel">
        <div className="agent-prompt-ai-header">
          <h3><i className="fa fa-magic" /> Improve Prompt with AI</h3>
          <button className="agent-prompt-ai-close-btn" onClick={onClose}><CloseIcon fontSize="small" /></button>
        </div>

        <div className="agent-prompt-ai-body">
          <p className="agent-prompt-ai-label">{ui['proposal'] ? 'Refine the proposal' : 'What should be improved?'}</p>
          <textarea
            className="agent-prompt-ai-feedback-input"
            value={ui['feedback']}
            onChange={(e) => updateUi({ feedback: e['target']['value'] })}
            placeholder={ui['proposal'] ? "Refine further — e.g. 'keep the changes but make the opening shorter'" : "Describe what to improve — e.g. 'agent talks too much', 'doesn't handle objections'"}
          />

          <div className="agent-prompt-ai-controls">
            <FormControl size="small" className="agent-prompt-ai-model-select">
              <Select value={ui['model']} onChange={(e) => updateUi({ model: e['target']['value'] })} displayEmpty>
                {MODELS.map((model) => <MenuItem key={model['id']} value={model['id']}>{model['label']}</MenuItem>)}
              </Select>
            </FormControl>
            <button className="btn-style-new agent-prompt-ai-generate-btn" onClick={handleGenerate} disabled={!ui['feedback'].trim() || ui['isProposing']}>
              {ui['isProposing'] ? <i className="fa fa-spinner fa-spin icon" /> : <i className="fa fa-magic icon" />}
              {ui['proposal'] ? 'Regenerate' : 'Generate improved prompt'}
            </button>
          </div>

          {ui['isProposing'] && (
            <div className="agent-prompt-ai-generating"><i className="fa fa-spinner fa-spin" /> Rewriting the prompt with your feedback…</div>
          )}

          {ui['proposal'] && !ui['isProposing'] && (
            <div className="agent-prompt-ai-proposal">
              <div className="agent-prompt-ai-proposal-header"><i className="fa fa-magic" /> Proposed prompt</div>
              <div className="agent-prompt-ai-proposal-content" dangerouslySetInnerHTML={{ __html: sanitizePromptHtml(ui['proposal']) }} />
            </div>
          )}
        </div>

        <div className="agent-prompt-ai-footer">
          <button className="btn-style-border" onClick={handleDiscard}>Discard</button>
          <button className="btn-style-new agent-prompt-ai-apply-btn" onClick={handleApply} disabled={!ui['proposal'] || ui['isProposing']}>Apply as new version</button>
        </div>
      </div>
    </Drawer>
  );
};

const MODELS = [
  { id: 'gpt-4o', label: 'GPT-4o' },
  { id: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  { id: 'claude-sonnet-4.6', label: 'Claude Sonnet 4.6' },
];

const INITIAL_UI = { feedback: '', proposal: '', feedbackTrail: [], isProposing: false, model: 'gpt-4o' };

const sanitizePromptHtml = (html) => DOMPurify.sanitize(html || '');

export default PromptAIUpdateDrawer;
